import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {Image, Pressable, View} from 'react-native';
import {NavigationContext} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import Tooltip from '@/Atoms/Tooltip';
import useGetPlayerProfileApi from '@/Hooks/PlayerProfileApi/useGetPlayerProfileApi';
import {PlayerProfileFull} from '@/Hooks/PlayerProfileApi/types';
import ENV from '../../../environment';

// How long a hover has to "dwell" before we (background-)fetch the profile
// -- avoids firing a request for every row a pointer sweeps across on its
// way down a leaderboard.
const HOVER_DEBOUNCE_MS = 300;

// The popover is portalled to document.body ~8px away from the trigger
// (Tooltip's own `padding={8}`), so the pointer is briefly over neither
// while crossing that gap. Grace period before actually closing -- canceled
// if the pointer lands back on the trigger OR the card in that window.
const CLOSE_INTENT_DELAY_MS = 180;

const CARD_WIDTH = 220;
const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#67B3E1', '#937ADB', '#D1AB41'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];

const navigateToProfile = (navigation: any, username: string) => {
  navigation?.navigate?.('PlayerProfile', {username});
};

// Headless -- mounted once we decide to fetch, calls the profile hook, and
// reports the *validated* result to the parent (a real PlayerProfileFull, or
// null for a still-loading/errored/404/empty-teaser/private response). Never
// renders anything itself: the parent only shows a popover once this reports
// a real profile, so there's no in-between loading/empty bubble to flash --
// and since the signal is purely "did we get real profile data back", this
// naturally also covers legacy Caissa/bot opponents (whose profile fetch
// 404s or comes back empty) with no bot-specific detection needed.
const ProfileFetcher = ({
  username,
  onResult,
}: {
  username: string;
  onResult: (profile: PlayerProfileFull | null) => void;
}) => {
  const {data, isLoading, isError} = useGetPlayerProfileApi(username);
  // Ref so the effect below doesn't need `onResult` in its dependency array
  // (it's re-created every parent render).
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const isFullProfile =
      !!data && !(data as any).login_nudge && !(data as any).is_private;
    onResultRef.current(
      isError || !isFullProfile ? null : (data as PlayerProfileFull),
    );
  }, [data, isLoading, isError]);

  return null;
};

// Purely presentational -- the parent only renders this once it already has
// a validated profile in hand, so there's no loading/error branch here.
// `onHoverIn`/`onHoverOut` let the parent know the pointer is on the card
// itself, the same way it tracks the trigger, so the close-intent timer
// waits for the pointer to actually leave both.
const ProfileCardContent = ({
  profile,
  username,
  navigation,
  onHoverIn,
  onHoverOut,
}: {
  profile: PlayerProfileFull;
  username: string;
  navigation: any;
  onHoverIn: () => void;
  onHoverOut: () => void;
}) => {
  const displayName = profile.name || profile.username || username;
  const totalGames =
    (profile.results_breakdown?.won ?? 0) +
    (profile.results_breakdown?.drawn ?? 0) +
    (profile.results_breakdown?.lost ?? 0);

  return (
    <Pressable
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      onPress={e => {
        e?.stopPropagation?.();
        navigateToProfile(navigation, username);
      }}
      style={{width: CARD_WIDTH}}>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            overflow: 'hidden',
            backgroundColor: avatarColor(displayName),
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {profile.avatar_url ? (
            <Image
              source={{uri: profile.avatar_url}}
              style={{width: 36, height: 36}}
            />
          ) : (
            <CCText color="#fff" fontWeight="600" fontSize={15}>
              {displayName.charAt(0).toUpperCase()}
            </CCText>
          )}
        </View>
        <View style={{flex: 1}}>
          <CCText
            color="#262322"
            fontWeight="600"
            fontSize={13}
            numberOfLines={1}>
            {displayName}
          </CCText>
          <CCText color="#888280" fontSize={11} numberOfLines={1}>
            @{profile.username}
          </CCText>
        </View>
      </View>
      <View style={{flexDirection: 'row', gap: 18, marginTop: 8}}>
        <View>
          <CCText color="#888280" fontSize={10}>
            Rating
          </CCText>
          <CCText color="#262322" fontWeight="600" fontSize={13}>
            {profile.gameplay_rating ?? '-'}
          </CCText>
        </View>
        <View>
          <CCText color="#888280" fontSize={10}>
            Games
          </CCText>
          <CCText color="#262322" fontWeight="600" fontSize={13}>
            {totalGames}
          </CCText>
        </View>
      </View>
    </Pressable>
  );
};

// Wraps a username/name text node: on a hover-capable pointer, dwelling for
// HOVER_DEBOUNCE_MS kicks off a background profile fetch; the popover only
// opens once that fetch actually resolves to a real profile -- a still-
// loading, failed, 404, or teaser/private response never shows anything (see
// ProfileFetcher above). On touch (no real "hover"), or a click anywhere in
// the wrapper or the (already-open) card, it navigates straight to
// `profile/<username>`. Wrap ONLY the name text, not the whole row -- the
// popover portals into `document.body` (see the Tooltip atom) so it never
// adds an overlay over surrounding UI (e.g. the board), but the *trigger*
// area should still be just the text.
//
// Interactive-tooltip pattern: closing is on a short delay, canceled if the
// pointer is over the trigger OR the card, so the mouse can actually travel
// the ~8px gap between them without the popover closing underneath it. This
// is direction-agnostic on purpose -- it only tracks "is the pointer over
// the trigger" / "...over the card", not which side of the trigger the card
// is on, so it keeps working unchanged whether Tooltip opens the card below
// (the common case) or flips it above (see `positions` below, for triggers
// near the top of the viewport). Open state is driven off Tooltip's real
// isOpen (via its `onOpenChange`), not a local "did we last open it" guess
// -- Tooltip can also close itself (onClickOutside, its own 10s safety
// timer), and mirroring the real value is what keeps a later hover from
// toggling it back open by mistake.
const PlayerProfilePreview = ({
  username,
  children,
  navigation: navigationProp,
}: {
  username?: string | null;
  children: React.ReactNode;
  // Optional -- most call sites sit inside a NavigationContainer and don't
  // need to pass this (falls back to context, mirroring `useNavigation()`).
  // Some hosts (e.g. LeaderboardTab) thread their own `navigation` prop
  // instead of relying on context (and are unit-tested without a
  // NavigationContainer at all), so accept the same prop and prefer it.
  navigation?: any;
}) => {
  // `useContext` (not the `useNavigation()` hook) so this never throws when
  // rendered outside a NavigationContainer -- it just falls back to
  // `navigationProp` (or, if neither is present, becomes a no-op).
  const contextNavigation = useContext(NavigationContext);
  const navigation = navigationProp ?? contextNavigation;

  const [shouldFetch, setShouldFetch] = useState(false);
  const [profileData, setProfileData] = useState<PlayerProfileFull | null>(
    null,
  );
  // Synchronous mirror of `profileData`, read from timer callbacks.
  const profileDataRef = useRef<PlayerProfileFull | null>(null);
  // 'idle' -> never fetched. 'pending' -> ProfileFetcher mounted, waiting on
  // it. 'resolved' -> got a definitive answer (valid profile OR null) --
  // don't kick off another fetch on a later hover; a valid one is already in
  // profileDataRef, an invalid one (e.g. a Caissa/legacy bot with no
  // profile) just silently stays that way.
  const fetchStatusRef = useRef<'idle' | 'pending' | 'resolved'>('idle');

  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tooltip's real isOpen, mirrored via onOpenChange -- read synchronously
  // from timer callbacks (state alone would be a stale closure there).
  const isOpenRef = useRef(false);
  // Whether the pointer is currently over the trigger / the card -- either
  // one being true keeps the popover open through the close-intent delay.
  const overTriggerRef = useRef(false);
  const overCardRef = useRef(false);
  // Tooltip's toggle(), captured fresh on every Tooltip render via the
  // children render-prop below -- lets fetch-result/card-hover handlers
  // (which aren't inside that render-prop) drive the same open/close.
  const toggleRef = useRef<(() => void) | null>(null);

  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [],
  );

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      if (
        !overTriggerRef.current &&
        !overCardRef.current &&
        isOpenRef.current
      ) {
        toggleRef.current?.();
      }
    }, CLOSE_INTENT_DELAY_MS);
  }, []);

  // Fired by ProfileFetcher once the fetch settles. Only opens the popover
  // if we got real data back AND the hover intent is still active -- if the
  // pointer already left before the (background) fetch resolved, we just
  // cache the result for next time instead of popping the card open late.
  const handleFetchResult = useCallback((profile: PlayerProfileFull | null) => {
    fetchStatusRef.current = 'resolved';
    profileDataRef.current = profile;
    setProfileData(profile);
    if (
      profile &&
      (overTriggerRef.current || overCardRef.current) &&
      !isOpenRef.current
    ) {
      toggleRef.current?.();
    }
  }, []);

  const handleTriggerHoverIn = useCallback(() => {
    overTriggerRef.current = true;
    clearCloseTimer();
    clearOpenTimer();
    if (isOpenRef.current) {
      return;
    }
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      if (profileDataRef.current) {
        // Already resolved to a real profile on an earlier hover -- open
        // right away, no need to wait on a fetch again.
        if (!isOpenRef.current) {
          toggleRef.current?.();
        }
      } else if (fetchStatusRef.current === 'idle') {
        fetchStatusRef.current = 'pending';
        setShouldFetch(true); // mounts ProfileFetcher; handleFetchResult opens on success
      }
      // else: already 'pending' (in flight) or 'resolved' with no profile
      // (e.g. a bot/legacy account) -- either way, nothing to do.
    }, HOVER_DEBOUNCE_MS);
  }, []);

  const handleTriggerHoverOut = useCallback(() => {
    overTriggerRef.current = false;
    clearOpenTimer();
    if (isOpenRef.current) {
      scheduleClose();
    }
  }, [scheduleClose]);

  const handleCardHoverIn = useCallback(() => {
    overCardRef.current = true;
    clearCloseTimer();
  }, []);

  const handleCardHoverOut = useCallback(() => {
    overCardRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const goToProfile = useCallback(
    (e?: any) => {
      e?.stopPropagation?.();
      if (!username) {
        return;
      }
      navigateToProfile(navigation, username);
    },
    [navigation, username],
  );

  // Mirrors the `ENV.PLAYER_PROFILE_ENABLED` release-gate check every other
  // `navigation.navigate('PlayerProfile', ...)` call site already does
  // (see e.g. Header, LeaderboardTab) -- when the route isn't registered,
  // don't offer a hover/click affordance that goes nowhere.
  if (!username || !ENV.PLAYER_PROFILE_ENABLED) {
    return <>{children}</>;
  }

  return (
    <>
      <Tooltip
        variant="card"
        // Prefer opening below the trigger, but flip to whichever side
        // actually has room (a top player bar in a live game has nothing
        // above it to open into; a bottom player bar, or a leaderboard row
        // near the bottom of the window, has nothing below) -- react-tiny-
        // popover walks this list in order and only advances past an entry
        // when the previous one would overflow the boundary.
        positions={['bottom', 'top', 'right', 'left']}
        // Keep a small gap from the viewport edge even after flipping, so
        // the card never touches it.
        boundaryInset={8}
        width={CARD_WIDTH + 32}
        onOpenChange={openNow => {
          isOpenRef.current = openNow;
        }}
        body={
          profileData ? (
            <ProfileCardContent
              profile={profileData}
              username={username}
              navigation={navigation}
              onHoverIn={handleCardHoverIn}
              onHoverOut={handleCardHoverOut}
            />
          ) : null
        }>
        {(toggle: () => void) => {
          toggleRef.current = toggle;
          return (
            <Pressable
              onHoverIn={handleTriggerHoverIn}
              onHoverOut={handleTriggerHoverOut}
              onPress={goToProfile}>
              {children}
            </Pressable>
          );
        }}
      </Tooltip>
      {shouldFetch && (
        <ProfileFetcher username={username} onResult={handleFetchResult} />
      )}
    </>
  );
};

export default PlayerProfilePreview;
